import { ApplicationTimeoutError } from "common/errors/ApplicationTimeoutError";
import { SubmittingError } from "common/errors/SubmittingError";
import { generate } from "common/random";
import { CheckoutParams, CheckoutStyles, InlineSplitElement } from "common/types";
import { FeatureFlag } from "common/utils/environment";
import { updateParamsToCheckoutParams } from "common/utils/parameters";
import { customFieldsToQuery, formatParameters, schema } from "common/validation/schema";
import ee from "event-emitter";
import { Emitter } from "event-emitter";
import { isEmpty, merge } from "lodash";
import { CheckoutType, OnlineBrand, PaymentType } from "univapay-node";

import {
    IFRAME_NAME,
    IFRAME_NAME_CONTAINER,
    IFRAME_ZINDEX,
    LOAD_TIMEOUT,
    SUBMIT_TIMEOUT,
    THREE_DS_IFRAME_ZINDEX,
} from "../../common/constants";
import { ConnectorError } from "../../common/errors/ConnectorError";
import { TimeoutError } from "../../common/errors/TimeoutError";
import {
    BaseMessageType,
    CheckoutUpdateOptions,
    CheckoutUpdateParameters,
    ConnectorMessage,
    EnhancedMessageEvent,
    Message,
    MessageBeforeClosingResponse,
    MessageExecute,
    MessageHandshake,
    MessageType,
    MessageUpdateParameters,
    ResourceType,
} from "../../common/Messages";
import { timeout } from "../../common/timeout";
import { development } from "../../common/utils/development";
import { JSONParse, JSONStringify } from "../../common/utils/json";
import { toArray, toBoolean } from "../../common/validation/utils";

import { getInputName, renderInput } from "./templates/ButtonTemplate";
import {
    EXCLUDED_PARAMETER_KEYS,
    PARAMETER_KEY_MAPPING,
    PARAMETER_VALUE_MAPPING,
    QUERY_PARAMS_TO_REMOVE,
} from "./constants";
import { BASE_CHECKOUT_URL, BASE_LINK_FORM_URL, FULL_CHECKOUT_URL, FULL_LINK_FORM_URL } from "./utils";

const cleanQueryParams = (query: string | URLSearchParams, keysToRemove: string[] = []): string => {
    const params = new URLSearchParams(query);

    // Remove by key
    for (const key of keysToRemove) {
        params.delete(key);
    }

    // special case based on the value of checkout
    if (params["checkout"] === CheckoutType.TOKEN) {
        params.delete("amount");
        params.delete("currency");
    }

    return params.toString();
};

const containerIframeStyles = {
    background: "rgba(0, 0, 0, 0.5)",
    width: "100vw",
    height: "100vh",
    top: "0",
    left: "0",
    position: "fixed",
    overflowX: "hidden",
    overflowY: "hidden",
    zIndex: IFRAME_ZINDEX,
};

const dialogIframeStyles = {
    background: "transparent",
    border: "0 none transparent",
    bottom: "0",
    display: "none",
    maxHeight: "100%",
    height: "100%",
    left: "0",
    overflowX: "hidden",
    overflowY: "auto",
    position: "fixed",
    right: "0",
    top: "0",
    visibility: "visible",
    maxWidth: "100%",
    width: "100%",
    zIndex: IFRAME_ZINDEX,
};

const inlineIFrameStyles = {
    border: "0 none transparent",
    display: "none",
    width: "100%",
    visibility: "visible",
};

const MD_BREAKPOINT = 768;
const isMdOrAbove = window.innerWidth >= MD_BREAKPOINT;

const generateDialogIFrameStyles = (isLinkForm?: boolean) => {
    if (!isLinkForm) {
        return dialogIframeStyles;
    }

    const sideMargin = 8;
    const maxWidth = 1080;
    const topMarginDesktop = 24;

    const sharedStyles = {
        ...dialogIframeStyles,
        width: `calc(100% - ${sideMargin * 2}px)`,
        maxWidth: `${maxWidth}px`,
    };

    if (isMdOrAbove) {
        return {
            ...sharedStyles,
            height: `calc(100% - ${topMarginDesktop * 2}px)`,
            top: `${topMarginDesktop}px`,
            left: `${sideMargin}px`,
            right: `${sideMargin}px`,
            margin: "0 auto",
        };
    } else {
        return {
            ...sharedStyles,
            top: `${sideMargin}px`,
            left: `${sideMargin}px`,
            right: `${sideMargin}px`,
            bottom: "auto",
        };
    }
};

export class Connector {
    private static instances: Connector[] = [];

    public emitter: Emitter;
    public iframe: HTMLIFrameElement;
    public iframeContainer: HTMLDivElement;
    public iframeId: string;

    private connectorId: string;
    private remote: Window;
    private connected = false;
    private initCallbacks: ((...params: unknown[]) => void)[] = [];
    private submitCallbacks: ((...params: unknown[]) => void)[] = [];
    private updateCallbacks: ((...params: unknown[]) => void)[] = [];
    private validateCallbacks: ((...params: unknown[]) => void)[] = [];
    public splitId?: string;
    public splitElements?: InlineSplitElement[];

    private currentParams: Partial<CheckoutParams> = {};

    private threeDsModal: HTMLIFrameElement;

    private originalOverflow: string = null;
    private windowResizeHandler: () => void = null;

    static initializedSplitIds: string[] = [];

    static addInstance(connector: Connector) {
        Connector.instances.push(connector);
    }

    static removeClosedInstance() {
        Connector.instances = Connector.instances.filter((instance) => !!instance.remote);
    }

    static getConnectorByIFrame(iframe: HTMLIFrameElement): Connector {
        if (!Connector.instances?.length) {
            throw new Error("No opened connector found.");
        }

        return Connector.instances.find(
            (instance) => instance.iframe === iframe || (instance.iframeId && instance.iframeId === iframe?.id)
        );
    }

    static getConnectorsBySplitId(splitId: string): Connector[] {
        if (!Connector.instances?.length) {
            throw new Error("No opened connector found.");
        }

        return Connector.instances.filter((instance) => instance.splitId === splitId);
    }

    constructor(params: Partial<CheckoutParams>) {
        this.connectorId = generate();
        this.emitter = ee({});
        this.emitter.on("checkout:execute", this.executeCheckout);
        this.currentParams = params;

        if (params.inlineSplitId) {
            const isMainSplitIdFrame = !Connector.initializedSplitIds.includes(params.inlineSplitId);
            if (isMainSplitIdFrame) {
                Connector.initializedSplitIds.push(params.inlineSplitId);
            }

            this.splitId = params.inlineSplitId;
            const parameterSplitElements =
                (Array.isArray(params.inlineSplitElements)
                    ? params.inlineSplitElements
                    : (params.inlineSplitElements?.split(",") as InlineSplitElement[])) ?? [];
            this.splitElements = [
                ...parameterSplitElements.filter((element) => element !== InlineSplitElement.MAIN),
                ...(isMainSplitIdFrame ? [InlineSplitElement.MAIN] : []),
            ];

            this.currentParams = { ...this.currentParams, inlineSplitElements: this.splitElements };
        }

        window.addEventListener("message", this.handleMessage);
    }

    private useLinkForm = () => {
        return this.currentParams?.responsive;
    };

    private constructLinkFormUrl = () => {
        const { paymentMethods, paymentType, paymentTypes, ...rest } = this.currentParams;

        // turn paymentType, paymentTypes and paymentMethods into paymentMethods
        const updatedPaymentMethods = toArray(
            paymentMethods || paymentTypes,
            paymentType ? [paymentType] : undefined
        ) as PaymentType[];
        const tokenOnly = toBoolean(rest.tokenOnly) || rest.checkout === CheckoutType.TOKEN;

        // Legacy support for old `address` parameter
        // TODO: revisit when we have more information about supporting the parameter
        const requireAddressFormLegacy =
            rest.address &&
            ([PaymentType.CARD, PaymentType.PAIDY] as (PaymentType | OnlineBrand | undefined)[]).includes(paymentType);
        const requireEmailFromLegacy =
            rest.address && ([PaymentType.ONLINE] as (PaymentType | OnlineBrand | undefined)[]).includes(paymentType);
        const requirePhoneNumberFromLegacy =
            rest.address && ([PaymentType.KONBINI] as (PaymentType | OnlineBrand | undefined)[]).includes(paymentType);

        this.currentParams = {
            ...rest,
            tokenOnly,
            paymentMethods: updatedPaymentMethods,
            requireBillingAddress: rest.requireBillingAddress || requireAddressFormLegacy,
            requireEmail: rest.requireEmail || requireEmailFromLegacy,
            requirePhoneNumber: rest.requirePhoneNumber || requirePhoneNumberFromLegacy,
        };

        // Always compute mapped values as they can change based on other values
        const sideEffectValues = Object.keys(PARAMETER_VALUE_MAPPING).reduce((acc, key) => {
            const mappedKey =
                (typeof PARAMETER_KEY_MAPPING[key] === "function"
                    ? PARAMETER_KEY_MAPPING[key](this.currentParams)
                    : PARAMETER_KEY_MAPPING[key]) || key;
            const mappedValue =
                (typeof PARAMETER_VALUE_MAPPING[key] === "function"
                    ? PARAMETER_VALUE_MAPPING[key]?.(this.currentParams, undefined)
                    : PARAMETER_VALUE_MAPPING[key]) || undefined;

            return acc.concat(`${mappedKey}=${mappedValue}`);
        }, []);

        const search = Object.keys(this.currentParams)
            .reduce((acc, key) => {
                const value = this.currentParams[key];

                if (EXCLUDED_PARAMETER_KEYS.includes(key)) {
                    return acc;
                }

                // review this later, might not be needed
                if (key === "inlineStyles") {
                    return acc.concat(
                        Object.entries(value).reduce<string[]>((acc, [k, v]) => {
                            if (v == null) return acc;
                            if (typeof v === "object" && v !== null) {
                                const nested = Object.entries(v)
                                    .reduce<string[]>((innerAcc, [nk, nv]) => {
                                        if (nv != null) innerAcc.push(`${nk}:${nv}`);
                                        return innerAcc;
                                    }, [])
                                    .join(";");
                                acc.push(`${k}=${nested}`);
                            } else {
                                acc.push(`${k}=${v}`);
                            }
                            return acc;
                        }, [])
                    );
                }

                if (key === "customFields") {
                    return acc.concat(
                        Object.entries(customFieldsToQuery(value)).reduce(
                            (acc, [key, value]) => acc.concat(`${key}=${value}`),
                            []
                        )
                    );
                }

                const formattedValue = (() => {
                    if (value === undefined || value === null || typeof value === "function") {
                        return undefined;
                    }

                    if (Array.isArray(value)) {
                        return value.filter(Boolean).join(",");
                    }

                    if (typeof value === "object") {
                        const subFields = Object.keys(value)
                            .filter((key) => value[key] !== undefined && value[key] !== null)
                            .reduce((acc, key) => acc.concat(`${key}=${value[key]}`), []);

                        return subFields?.length ? subFields : undefined;
                    }

                    return `${value}`;
                })();

                if (formattedValue !== null && formattedValue !== undefined && formattedValue !== "") {
                    // For query params that have different names in form checkout
                    const mappedKey =
                        (typeof PARAMETER_KEY_MAPPING[key] === "function"
                            ? PARAMETER_KEY_MAPPING[key](this.currentParams)
                            : PARAMETER_KEY_MAPPING[key]) || key;
                    const mappedValue =
                        (typeof PARAMETER_VALUE_MAPPING[key] === "function"
                            ? PARAMETER_VALUE_MAPPING[key]?.(this.currentParams, formattedValue)
                            : PARAMETER_VALUE_MAPPING[key]) || formattedValue;

                    return acc.concat(`${mappedKey}=${mappedValue}`);
                }

                return acc;
            }, sideEffectValues)
            .join("&");

        return `${FULL_LINK_FORM_URL}?${cleanQueryParams(search, QUERY_PARAMS_TO_REMOVE)}`;
    };

    open = async (parameterOverrides: CheckoutParams & CheckoutStyles, parentNode?: HTMLElement) => {
        this.currentParams = merge({}, parameterOverrides, this.currentParams);

        const promise = Promise.race([
            new Promise((resolve, reject) => {
                this.initCallbacks = [resolve, reject];
            }),
            timeout(LOAD_TIMEOUT, new ApplicationTimeoutError(LOAD_TIMEOUT), () => {
                this.initCallbacks = [];
            }),
        ]);

        development(() => console.info("1- Opening checkout..."));
        const checkout = this.createIframe(parentNode);

        this.remote = (checkout as HTMLIFrameElement).contentWindow;

        if (this.iframe) {
            this.iframe.style.display = "block";
        }

        // This in needed in order to dynamically update the iframe styles on window resize
        if (!parentNode && this.useLinkForm()) {
            this.windowResizeHandler = () => {
                if (!this.iframe) return;
                const currentDisplay = this.iframe.style.display;
                const currentHeight = this.iframe.style.height;
                const styles = generateDialogIFrameStyles(this.useLinkForm());
                Object.keys(styles).forEach((key: string) => (this.iframe.style[key] = styles[key]));
                this.iframe.style.display = currentDisplay;

                // On small screens the height should take only the content height
                const isSmallViewport = window.innerWidth < MD_BREAKPOINT;
                if (isSmallViewport) {
                    this.iframe.style.height = currentHeight;
                }
            };
            window.addEventListener("resize", this.windowResizeHandler);
        }

        await promise;
        this.finishConnect();

        Connector.addInstance(this);

        if (!parentNode) {
            // overflow is hidden for dialog, not for inline style with a parent
            this.originalOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
        }

        return checkout as HTMLIFrameElement;
    };

    async submit() {
        if (this.submitCallbacks.length) {
            console.warn("Form already submitting. Please wait.");
            return Promise.reject(new SubmittingError("FORM_ALREADY_SUBMITTING"));
        }

        this.sendMessage({ type: MessageType.SUBMIT_CARD_DATA, data: { connectorId: this.connectorId } });

        return Promise.race([
            new Promise((resolve, reject) => {
                this.submitCallbacks = [resolve, reject];
            }),
            timeout(SUBMIT_TIMEOUT, new TimeoutError(SUBMIT_TIMEOUT), () => {
                this.submitCallbacks = [];
            }),
        ]);
    }

    async update(overrides: CheckoutUpdateParameters, options?: CheckoutUpdateOptions) {
        if (!overrides) {
            return;
        }

        const newParams = formatParameters({
            ...this.currentParams,
            ...updateParamsToCheckoutParams(overrides),
        } as CheckoutParams);

        try {
            schema.validateSync(newParams);
        } catch (errors) {
            console.error("Invalid override object found when updating the inline form. Skipping.", errors);
            this.emitter.emit("checkout:update-validation-errors", { errors });
            throw errors;
        }

        this.sendMessage({
            type: MessageType.UPDATE_PARAMETERS,
            data: { connectorId: this.connectorId, overrides, options } as MessageUpdateParameters,
        });
        this.currentParams = newParams;

        return Promise.race([
            new Promise((resolve, reject) => {
                this.updateCallbacks = [resolve, reject];
            }),
            timeout(SUBMIT_TIMEOUT, new TimeoutError(SUBMIT_TIMEOUT), () => {
                this.updateCallbacks = [];
            }),
        ]);
    }

    async validate() {
        if (this.validateCallbacks.length) {
            console.warn("Form already validating. Please wait.");
            return Promise.reject(new Error("FORM_ALREADY_VALIDATING"));
        }

        this.sendMessage({
            type: MessageType.VALIDATE_FORM,
            data: { connectorId: this.connectorId } as BaseMessageType,
        });

        return Promise.race([
            new Promise((resolve, reject) => {
                this.validateCallbacks = [resolve, reject];
            }),
            timeout(SUBMIT_TIMEOUT, new TimeoutError(SUBMIT_TIMEOUT), () => {
                this.validateCallbacks = [];
            }),
        ]);
    }

    close(): void {
        this.connected = false;

        // Do not close for inline form
        if (!this.remote) {
            return;
        }

        if (this.windowResizeHandler) {
            window.removeEventListener("resize", this.windowResizeHandler);
            this.windowResizeHandler = null;
        }

        document.body.style.overflow = this.originalOverflow;
        this.remote.close();
        this.remote = null;

        if (this.iframe) {
            if (this.iframeContainer) {
                window.document.body.removeChild(this.iframeContainer);
            } else {
                window.document.body.removeChild(this.iframe);
            }
        } else {
            Connector.removeClosedInstance();
        }
    }

    private createIframe(parent: HTMLElement): HTMLIFrameElement {
        const isLinkForm = this.useLinkForm();

        const iframe: HTMLIFrameElement = window.document.createElement("iframe");
        iframe.frameBorder = "0";
        iframe.name = IFRAME_NAME;
        iframe.className = IFRAME_NAME;
        iframe.src = isLinkForm ? this.constructLinkFormUrl() : FULL_CHECKOUT_URL;
        iframe.setAttribute("allowtransparency", "true");
        if (isLinkForm) {
            // Allows sending origin for connector messages to cross-origin link form
            iframe.referrerPolicy = "origin";
        }
        const iframeId = `${IFRAME_NAME}-${generate(8)}`;
        iframe.id = iframeId;
        this.iframeId = iframeId;
        const styles = parent ? inlineIFrameStyles : generateDialogIFrameStyles(this.useLinkForm());
        Object.keys(styles).forEach((key: string) => (iframe.style[key] = styles[key]));
        this.iframe = iframe;

        if (isLinkForm && !parent) {
            const iFrameContainer = window.document.createElement("div");
            iFrameContainer.className = IFRAME_NAME_CONTAINER;
            Object.keys(containerIframeStyles).forEach(
                (key: string) => (iFrameContainer.style[key] = containerIframeStyles[key])
            );
            window.document.body.appendChild(iFrameContainer);
            this.iframeContainer = iFrameContainer;
        } else {
            this.iframeContainer = parent as HTMLDivElement;
        }

        (parent || this.iframeContainer || window.document.body).appendChild(iframe);

        return iframe;
    }

    private createThreeDsIframe(): HTMLIFrameElement {
        const iFrame = window.document.createElement("iframe");
        iFrame.name = "three-ds-i-frame-client-wrapper";
        iFrame.style.width = "100vw";
        iFrame.style.height = "100vh";
        iFrame.style.top = "0%";
        iFrame.style.left = "0%";
        iFrame.style.position = "fixed";
        iFrame.style.display = "block";
        iFrame.style.background = "rgba(0, 0, 0, 50%)";
        iFrame.style.border = "0";
        iFrame.frameBorder = "0";
        iFrame.style.zIndex = `${THREE_DS_IFRAME_ZINDEX}`;
        iFrame.setAttribute("allowtransparency", "true");

        const base = FeatureFlag.linkFormRedirect()
            ? new URL(BASE_LINK_FORM_URL).origin
            : new URL(BASE_CHECKOUT_URL).origin;

        iFrame.setAttribute("src", `${base}/redirect/index.html?type=three-ds`);
        window.document.body.appendChild(iFrame);

        this.threeDsModal = iFrame;

        return iFrame;
    }

    private finishConnect = (connectorId?: string): void => {
        this.initCallbacks = [];

        if (connectorId === this.connectorId) {
            this.executeCheckout();
        }
    };

    private handleMessage = (e: EnhancedMessageEvent): void => {
        if (!e || (FULL_CHECKOUT_URL.indexOf(e.origin) === -1 && FULL_LINK_FORM_URL.indexOf(e.origin) === -1)) {
            return;
        }

        if (typeof e.data === "object" && e.data.type === "webpackOk") {
            return;
        }

        try {
            const message = JSONParse<ConnectorMessage>(e.data);
            if (!message) {
                return;
            }

            switch (message.type) {
                case MessageType.HANDSHAKE: {
                    const data = message.data;

                    if (!data.paired || (data.paired && this.connectorId === data.connectorId)) {
                        this.handleHandshake(message);
                    }
                    break;
                }

                case MessageType.OPENED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:opened");
                    }
                    break;

                case MessageType.BEFORE_CLOSING:
                    if (message.data.connectorId === this.connectorId) {
                        // Prevent closing for false only. Other falsy value should close the widget
                        const shouldClose = this.currentParams?.beforeClosing?.() !== false;

                        this.sendMessage({
                            type: MessageType.BEFORE_CLOSING_RESPONSE,
                            data: { connectorId: this.connectorId, shouldClose } as MessageBeforeClosingResponse,
                        });
                    }
                    break;

                case MessageType.CLOSED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:closed", {
                            failedSubscriptionId: message.data.failedSubscriptionId,
                        });
                    }
                    break;

                case MessageType.SUCCESS:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:success", message.data);
                    }
                    break;

                case MessageType.ERROR:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:error", message.data);
                    }
                    break;

                case MessageType.TOKEN_CREATED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:token-created", message.data);
                    }
                    break;

                case MessageType.CHARGE_CREATED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:charge-created", message.data);
                    }
                    break;

                case MessageType.SUBSCRIPTION_CREATED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:subscription-created", message.data);
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:three-ds-authorization", message.data);
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION_SUCCESS:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:three-ds-authorization-success", message.data);

                        if (this.threeDsModal) {
                            this.threeDsModal.remove();
                            this.threeDsModal = null;
                        }
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION_FAILURE:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:three-ds-authorization-failure", message.data);

                        setTimeout(() => {
                            if (this.threeDsModal) {
                                this.threeDsModal.remove();
                                this.threeDsModal = null;
                            }
                        }, 4000); // Give some time for the customer to read: https://github.com/univapaycast/gopay-checkout/issues/1397
                    }
                    break;

                case MessageType.VALIDATION_ERROR:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:validation-error", message.data);
                    }
                    break;

                case MessageType.RESIZE:
                    if (message.data.connectorId === this.connectorId) {
                        const isModal =
                            !this.iframeContainer || this.iframeContainer.className === IFRAME_NAME_CONTAINER;
                        if (isModal) {
                            break;
                        }

                        const formattedHeight = `${message.data.height}`.replace("px", "") + "px";
                        this.iframe.style.height = formattedHeight;
                        this.iframe.height = message.data.height;
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION_MODAL: {
                    if (message.data.connectorId === this.connectorId && !this.threeDsModal) {
                        const iFrame = this.createThreeDsIframe();

                        const onThreeDsIFrameMessage = (event: MessageEvent) => {
                            if (event.source !== iFrame?.contentWindow) {
                                return;
                            }

                            if (event.data.type === "THREE_DS_WRAPPER_INITIALIZATION_OPENED") {
                                iFrame?.contentWindow?.postMessage(
                                    { type: "THREE_DS_WRAPPER_INITIALIZATION", ...message.data },
                                    "*"
                                );
                            } else if (event.data.type === "THREE_DS_MODAL_OPENED") {
                                // At this point listener has received all expected messages so we can safely remove it
                                window.removeEventListener("message", onThreeDsIFrameMessage);

                                this.emitter.emit("checkout:three-ds-authorization-modal-opened", message.data);
                            }
                        };

                        window.addEventListener("message", onThreeDsIFrameMessage);
                    }
                    break;
                }

                case MessageType.PARAMETERS_UPDATED: {
                    if (message.data.connectorId === this.connectorId) {
                        const [resolve, reject] = this.updateCallbacks;

                        if (message.errors) {
                            reject?.(message.errors);
                        } else {
                            resolve?.(message.data);
                        }

                        this.updateCallbacks = [];
                    }
                    break;
                }

                case MessageType.FORM_VALIDATED:
                    if (message.data.connectorId === this.connectorId) {
                        const [resolve, reject] = this.validateCallbacks;

                        if (message.errors) {
                            reject?.(message.errors);
                        } else {
                            if (!isEmpty(message.data?.errors)) {
                                this.emitter.emit("checkout:validation-error", { errorFields: message.data?.errors });
                            }

                            resolve?.(message.data);
                        }

                        this.validateCallbacks = [];
                    }
                    break;

                case MessageType.SUBMITTED: {
                    if (message.data.connectorId === this.connectorId) {
                        const [resolve, reject] = this.submitCallbacks;

                        if (message.errors) {
                            reject?.(message.errors);
                        } else {
                            const hiddenTokenInput = renderInput(
                                getInputName(ResourceType.TRANSACTION_TOKEN),
                                message.data.token
                            );
                            this.iframe.parentElement.appendChild(hiddenTokenInput);

                            if (ResourceType.CHARGE in message.data) {
                                const hiddenChargeInput = renderInput(
                                    getInputName(ResourceType.CHARGE),
                                    message.data[ResourceType.CHARGE] as string
                                );
                                this.iframe.parentElement.appendChild(hiddenChargeInput);
                            }

                            if (ResourceType.SUBSCRIPTION in message.data) {
                                const hiddenSubscriptionInput = renderInput(
                                    getInputName(ResourceType.SUBSCRIPTION),
                                    message.data[ResourceType.SUBSCRIPTION] as string
                                );
                                this.iframe.parentElement.appendChild(hiddenSubscriptionInput);
                            }

                            resolve?.(message.data);
                        }

                        this.submitCallbacks = [];
                    }
                    break;
                }

                default:
                    return;
            }
        } catch (e) {
            this.connected = false;
            this.connectorId = null;
            this.remote = null;

            console.error(e);
            throw new ConnectorError();
        }
    };

    private sendMessage(message: Message<BaseMessageType>): void {
        this.remote.postMessage(
            JSONStringify({ ...message, data: { ...message.data, connectorId: this.connectorId } }),
            this.useLinkForm() ? FULL_LINK_FORM_URL : FULL_CHECKOUT_URL
        );
    }

    private executeCheckout = (): void => {
        this.sendMessage({
            data: {
                connectorId: undefined,
                params: this.currentParams,
                appId: this.currentParams.appId,
                checkout: this.currentParams.checkout,
            } as MessageExecute,
            type: MessageType.EXECUTE,
        });
    };

    private handleHandshake(message: Message<MessageHandshake>): void {
        if (this.connected) {
            return;
        }

        if (message.data.paired) {
            development(() => console.info(`5- Paired (id: ${message.data.connectorId})`, message));
            const [resolve] = this.initCallbacks;
            this.connected = true;

            if (typeof resolve === "function") {
                resolve(message.data.connectorId);
            } else {
                this.finishConnect(message.data.connectorId);
            }
        } else if (this.remote) {
            development(() => console.info(`3- Receiving handshake (id: ${message.data.connectorId})`));
            this.sendMessage(message);
        }
    }
}
